import * as React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth/types';
import CreateValidationFormModal from 'src/features/validations/components/forms/CreateValidationFormModal';
import { validationMessages } from 'src/features/validations/utils/messages';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import {
    DialogBody,
    DialogClose,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useGetActiveStay } from 'src/features/stay/api/useGetActiveStay';
import { useParking } from 'src/providers/ParkingProvider';
import { useListActiveStayPeriods } from 'src/features/stay/api/useListActiveStayPeriods';
import { useListGatewayPasses } from 'src/features/gatewayPass/api/useListGatewayPasses';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import SidePanelTabs from 'src/features/stay/components/sidePanel/SidePanelTabs';
import ActiveStayPeriods from 'src/features/stay/components/sidePanel/ActiveStayPeriods';
import ImageCarousel from 'src/features/visitor/components/carousel/ImageCarousel';
import { formatTimestampToCustomId } from 'src/features/visitor/utils/timestampToId';
import ValidationOverviewActiveStays from 'src/features/validations/components/ValidationOverviewActiveStays';
import useSorting from 'src/registry/hooks/useSorting';

type ActiveStaysSidePanelProps = {
    selectedStayId?: string;
};

export default function ValidationsSidePanel({ selectedStayId }: ActiveStaysSidePanelProps) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const manageValidationPermission = useHasPermissions([PERMISSION.ALLOW_VALIDATIONS]);
    const { querySortingOptions } = useSorting({
        initialSortingState: [{ id: 'start', desc: true }],
    });

    const { data: selectedActiveStay } = useGetActiveStay({
        stayId: selectedStayId ?? '',
        parkingId,
        config: {
            enabled: !!selectedStayId || !!parkingId,
        },
    });

    const { data: activeStayPeriodsData } = useListActiveStayPeriods({
        stayId: selectedStayId ?? '',
        parkingId,
        config: { enabled: !!selectedStayId || !!parkingId },
        ...querySortingOptions,
    });

    const { data: filteredGatewayPassesByStayId } = useListGatewayPasses({
        active_stay: selectedStayId ?? '',
        parkingId,
        limit: 500,
        config: { enabled: !!selectedStayId || !!parkingId },
    });

    const gatewayPassesPhotos = React.useMemo(() => {
        return filteredGatewayPassesByStayId?.results.map((pass) => {
            return {
                image: pass.photo,
                title: pass.gateway.translations.find((z) => z.language === locale)?.name,
                subtitle: `${formatMessage(visitorMessages.pass)} ${formatTimestampToCustomId(pass.time)}`,
            };
        });
    }, [filteredGatewayPassesByStayId?.results, formatMessage, locale]);

    const activePeriodsResults = React.useMemo(
        () => (activeStayPeriodsData ? activeStayPeriodsData.results : []),
        [activeStayPeriodsData],
    );

    return (
        <>
            {selectedActiveStay ? (
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <DialogTitle className="pr-0 font-mono">
                            {formatMessage(visitorMessages.parkingStay)}
                        </DialogTitle>
                        <Badge theme="green">
                            <FontAwesomeIcon icon={ICON_MAP.WAVE_PULSE} />
                            {formatMessage(visitorMessages.activeStay)}
                        </Badge>
                    </div>
                    <DialogDescription>{`#${selectedActiveStay.ref}`}</DialogDescription>
                </DialogHeader>
            ) : (
                <DialogHeader>
                    <Skeleton className="h-7 w-32" />
                    <Skeleton className="h-5 w-60" />
                </DialogHeader>
            )}

            <DialogSeparator />
            <DialogBody>
                <ImageCarousel imageSlides={gatewayPassesPhotos} />
                {manageValidationPermission && (
                    <CreateValidationFormModal
                        selectedStay={selectedActiveStay}
                        selectedStayPeriod={activePeriodsResults[activePeriodsResults.length - 1]}
                    >
                        <Button className="cursor-pointer">{formatMessage(validationMessages.validate)}</Button>
                    </CreateValidationFormModal>
                )}
                <SidePanelTabs
                    tabs={[
                        {
                            key: 'overview',
                            label: formatMessage(visitorMessages.overview),
                            content: (
                                <ValidationOverviewActiveStays
                                    selectedStay={selectedActiveStay}
                                    selectedStayPeriod={activePeriodsResults[activePeriodsResults.length - 1]}
                                />
                            ),
                        },
                        {
                            key: 'periods',
                            label: formatMessage(visitorMessages.periods),
                            content: <ActiveStayPeriods stayPeriodsData={activePeriodsResults} />,
                        },
                    ]}
                    defaultActiveKey="overview"
                />
            </DialogBody>
            <DialogFooter>
                <DialogClose asChild>
                    <Button variant="outline" className="flex-1">
                        {formatMessage(generalMessages.close)}
                    </Button>
                </DialogClose>
            </DialogFooter>
        </>
    );
}
